import { useEffect, useState } from 'react';
import type { StructureType } from '../lib/api';

interface SubtypeContent {
  title: string;
  /** Path (below `/RESOURCES/CastIron`) of the structural rendering thumbnail. */
  rendering: string;
  label: string;
}

const BASE_LOCATION = '/RESOURCES/CastIron';

/**
 * Per-subtype "More Information" content. Mirrors the legacy
 * `resources/jsx/components/docs/castiron{round,flatrect,curbbox}docs.jsx`
 * modals, each of which showed a single structural rendering thumbnail.
 */
function contentFor(type: StructureType): SubtypeContent | null {
  if (type.isCastIronRound) {
    return {
      title: 'Round Frame and\u00a0Grate',
      rendering: 'typeRD.jpg',
      label: 'Round Frame and\u00a0Grate',
    };
  }
  if (type.isCastIronRectangular) {
    return {
      title: 'Flat-Rectangular Frame and\u00a0Grate',
      rendering: 'typeSQ.jpg',
      label: 'Flat-Rectangular Frame and\u00a0Grate',
    };
  }
  if (type.isCastIronCurbBoxCombo) {
    return {
      title: 'Curb Box\u00a0Combination',
      rendering: 'typeSQCB.jpg',
      label: 'Curb Box\u00a0Combination',
    };
  }
  return null;
}

/**
 * "More Information" overlay for an individual Cast Iron subtype. Mirrors the
 * legacy per-subtype docs modal: a single "Structural Renderings" section with
 * one clickable thumbnail that opens a full-screen lightbox.
 */
export function SubtypeInfoModal({
  type,
  onClose,
}: {
  type: StructureType;
  onClose: () => void;
}) {
  const content = contentFor(type);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxOpen) setLightboxOpen(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, lightboxOpen]);

  if (!content) return null;
  const src = `${BASE_LOCATION}/${content.rendering}`;

  return (
    <>
      <div
        className="modal fade show"
        style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1055,
          overflow: 'auto',
        }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="modal-dialog modal-dialog-scrollable modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn-close p-2"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div className="modal-body ps-2 ps-lg-5">
              <h1>{content.title}</h1>

              <h2 className="d-flex align-items-center mt-4">
                <img
                  className="img-fluid pe-2"
                  src="/images/ICON/Icon_Transform_Small.png"
                  alt=""
                  style={{ height: 28, width: 'auto' }}
                />
                <span className="pt-1">Structural Renderings</span>
                <img
                  className="img-fluid ps-2"
                  src="/images/ICON/Icon_Question_Filled_Green.png"
                  alt=""
                  title="3D renderings of this structure type"
                  style={{ height: 24, width: 'auto', cursor: 'help' }}
                />
              </h2>
              <div className="text-primary fs-5 text-start">Click to Expand</div>

              <div className="row text-center align-items-center">
                <div className="col-12 col-md-6 col-lg-3 mb-3">
                  <div className="align-items-center" style={{ height: 70 }}>
                    <a style={{ cursor: 'zoom-in' }} onClick={() => setLightboxOpen(true)}>
                      <img
                        className="img-fluid"
                        style={{ maxHeight: 70, height: 'auto', width: 'auto' }}
                        src={src}
                        alt={content.label}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </a>
                  </div>
                  <a style={{ cursor: 'zoom-in' }} onClick={() => setLightboxOpen(true)}>
                    <p className="fs-6 pt-2">{content.label}</p>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightboxOpen && (
        <div
          onClick={() => setLightboxOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.92)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 24,
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
        >
          <img
            src={src}
            alt=""
            style={{
              maxWidth: '95vw',
              maxHeight: '95vh',
              height: 'auto',
              width: 'auto',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="btn-close btn-close-white"
            aria-label="Close"
            onClick={() => setLightboxOpen(false)}
            style={{ position: 'absolute', top: 16, right: 16 }}
          />
        </div>
      )}
    </>
  );
}
